import { Theme, useMediaQuery } from '@mui/material';
import { CURRENT_AVAILABLE_GROUPS, useAuthContext, useHasAuthGroup } from 'providers/auth';
import { matchPath, useLocation } from 'react-router';
import routes from 'routes';

/**
 * Routes that fit the viewport and scroll per column instead of growing the page. Add a pattern
 * to opt a route in — but only once the page itself is a bounded flex column, or its content
 * will be clipped rather than scrolled.
 */
const FULL_HEIGHT_PATHS = [
  `${routes.company.campaign.path}/${routes.company.campaign.chatConversation.path}`,
  `${routes.company.campaign.path}/${routes.company.campaign.conversation.path}`,
  `${routes.campaign.path}/${routes.campaign.conversation.path}`,
  // The standalone inbox, registered once per persona: under the company for internal users and
  // at the root for clients. Both render the same ConversationPage.
  `${routes.company.path}/${routes.company.conversations.path}`,
  `/${routes.company.conversations.path}`,
];

export const useFullHeightRoute = (): boolean => {
  const { pathname } = useLocation();
  const { isInternal } = useAuthContext();
  const isCallCenterRep = useHasAuthGroup(CURRENT_AVAILABLE_GROUPS.CustomerCallCenterRep);
  // Below md the columns stack, so a viewport-locked shell would leave them fighting over one
  // screen. Those breakpoints keep today's page-grows-and-scrolls behavior.
  const isDesktop = useMediaQuery<Theme>((theme: Theme) => theme.breakpoints.up('md'));

  const matches = (path: string) => matchPath(path, pathname) !== null;

  // A call-center rep's home IS the inbox; for anyone else "/" is the dashboard, which scrolls
  // the page. Mirrors the route tree in router.tsx, which gates that home on both checks.
  const repHome = !isInternal && isCallCenterRep && matches(routes.home.path);

  return isDesktop && (repHome || FULL_HEIGHT_PATHS.some(matches));
};
